import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
	Box,
	Alert,
	Typography,
	FormControl,
	Select,
	MenuItem,
	InputLabel,
	Button,
} from "@mui/material";
import * as hooks from "lib/hooks";
import * as result from "lib/result";
import { GetSelectStyle } from "lib/styles/textField";
import { UseHasPermission } from "lib/hooks/useHasPermission";
import { useSelector } from "react-redux";
import { RootState, UserSlice } from "store";
import { SelectUserSectors } from "store/slices/userSearchSlice";
import { BusinessObj } from "auth";
import {
	AccessibleSector,
	AccessiblePlant,
	SectorData,
	UpdatePlantUserRequest,
} from "models/dto/user";
import { SectorTypePlantPermissionTable } from "../SectorTypePlantPermissionTable";
import { RemoveSectorsConfirmation } from "../RemoveSectorsConfirmation";
import { UserManagementDialogShell } from "../UserManagementDialogShell";
import {
	WouldUserHaveNoAccessAfterChange,
	GetIncompleteSectorsWarning,
	GetIncompleteSectorsWarningMessage,
	ComputeRoleObjDelta,
} from "../../utils/userAccessUtils";
import { OBJECT_ID_MANAGE_USER } from "../../constants";

/** Stable serialization of valid sector data for change detection. */
function serializeValidSectorData(data: Record<string, SectorData>): string {
	const valid = Object.entries(data)
		.filter(
			([, s]) =>
				s.plants.length > 0 &&
				s.plants.every((p) => (p.permissions?.length ?? 0) > 0)
		)
		.sort(([a], [b]) => a.localeCompare(b))
		.map(([id, s]) => ({
			sectorId: id,
			plants: [...s.plants]
				.sort((a, b) => (a.plantId || "").localeCompare(b.plantId || ""))
				.map((p) => ({
					plantId: p.plantId,
					permissions: [...(p.permissions || [])]
						.sort((a, b) => (a.businessObjectId || "").localeCompare(b.businessObjectId || ""))
						.map((perm) => ({ ...perm })),
				})),
		}));
	return JSON.stringify(valid);
}

interface formData {
	username: string;
	email: string;
	gmin: string;
	name: string;
}

interface validationState {
	email: { valid: boolean; message: string };
}

interface editPlantUserDialogProps {
	open: boolean;
	gmin: string | null;
	onClose: () => void;
	onSuccess?: () => void;
	/** Sector IDs where this user is already a Sector Admin; these are excluded from the "Add sector" dropdown to prevent duplicate assignment. */
	sectorIdsUserIsSectorAdminFor?: string[];
}

export function EditPlantUserDialog({
	open,
	gmin,
	onClose,
	onSuccess,
	sectorIdsUserIsSectorAdminFor = [],
}: editPlantUserDialogProps) {
	const api = hooks.UseMccApi();
	const snack = hooks.UseSnackBar();
	const permissions = UseHasPermission();
	const businessObjList = useSelector(UserSlice.selectors.selectBusinessObjList) as BusinessObj[];
	const currentUser = useSelector(UserSlice.selectors.selectUser) as { id?: string } | null;
	const currentUserId = currentUser?.id ?? "";
	const isEditingSelf = Boolean(gmin && currentUserId && gmin === currentUserId);
	// Full set of sectors this user is in (any role) from user search — used to decide if they would have no access to MCC after save
	const fullUserSectors = useSelector((state: RootState) =>
		gmin ? SelectUserSectors(state, gmin) : null
	);

	const [formData, setFormData] = useState<formData>({
		username: "",
		email: "",
		gmin: "",
		name: "",
	});

	// Validation state - email is read-only and always valid when loaded
	const [validation] = useState<validationState>({
		email: { valid: true, message: "" },
	});

	const [selectedSectors, setSelectedSectors] = useState<string[]>([]);
	const [sectorData, setSectorData] = useState<Record<string, SectorData>>({});
	const [selectedSectorForAdd, setSelectedSectorForAdd] = useState<string>("");
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string>("");
	const [initialLoad, setInitialLoad] = useState(true);
	const [deleteConfirmationChecked, setDeleteConfirmationChecked] = useState(false);
	const [initialSectorCount, setInitialSectorCount] = useState(0);
	const [initialSectorIds, setInitialSectorIds] = useState<string[]>([]);
	/** Snapshot of sector data at load for delta computation (only valid sectors). */
	const [initialSectorDataSnapshot, setInitialSectorDataSnapshot] = useState<Record<string, SectorData> | null>(null);
	// Full list of sectors/plants from API (all permissions the plant user has) for messaging
	const [allSectorsFromApi, setAllSectorsFromApi] = useState<SectorData[]>([]);

	// Sectors where current user has Manage User (S1) — from permissions API (generic by objectId).
	const accessibleSectors: AccessibleSector[] = useMemo(
		() => permissions.getAccessibleSectorsForObject(OBJECT_ID_MANAGE_USER),
		[permissions.getAccessibleSectorsForObject]
	);

	// Sectors that can be added in this dialog (exclude sectors where user is already Sector Admin)
	const sectorsAvailableForAdd = useMemo(
		() =>
			accessibleSectors.filter(
				(s) => !sectorIdsUserIsSectorAdminFor.includes(s.id)
			),
		[accessibleSectors, sectorIdsUserIsSectorAdminFor]
	);

	// Plants from permissions API (S1 only), grouped by sector. No sector vs Plant Users Access Manager branching.
	const sectorTypePlants = useMemo(() => {
		const sectorNamesSet = new Set(
			accessibleSectors.map((s) => s.name)
		);
		const plantsBySector: Record<string, AccessiblePlant[]> = {};

		businessObjList.forEach((bo) => {
			if (bo.GetBusinessObjTypeString() !== OBJECT_ID_MANAGE_USER) return;

			const plantId = (bo as unknown as { readonly plantId: string }).plantId;
			const plantName = (bo as unknown as { readonly plantName: string }).plantName;
			const sectorName = bo.SectorName;

			if (!plantId || !plantName || !sectorName || !sectorNamesSet.has(sectorName))
				return;

			const sector = accessibleSectors.find((s) => s.name === sectorName);
			if (!sector) return;

			if (!plantsBySector[sector.id]) {
				plantsBySector[sector.id] = [];
			}
			if (!plantsBySector[sector.id].some((p) => p.id === plantId)) {
				plantsBySector[sector.id].push({ id: plantId, name: plantName });
			}
		});

		Object.keys(plantsBySector).forEach((sectorId) => {
			plantsBySector[sectorId].sort((a, b) => a.name.localeCompare(b.name));
		});

		return plantsBySector;
	}, [businessObjList, accessibleSectors]);

	// Reset form when dialog closes
	useEffect(() => {
		if (!open) {
			setFormData({
				username: "",
				email: "",
				gmin: "",
				name: "",
			});
			setSelectedSectors([]);
			setSectorData({});
			setError("");
			setInitialLoad(true);
			setDeleteConfirmationChecked(false);
			setInitialSectorCount(0);
			setInitialSectorIds([]);
			setAllSectorsFromApi([]);
		}
	}, [open]);

	// Load user data when dialog opens
	useEffect(() => {
		if (!open || !gmin) return;

		const loadUserData = async () => {
			try {
				// If we already have data for this gmin (e.g. quick reopen), fetch in background to avoid content→loading→content flash
				const alreadyShowingThisUser =
					formData.gmin === gmin && Object.keys(sectorData).length > 0;
				if (!alreadyShowingThisUser) {
					setLoading(true);
				}
				setError("");

				// Load user data
				const userData = await api.GetPlantUser(gmin);

				setFormData({
					username: userData.username || "",
					email: userData.email || "",
					gmin: userData.gmin || "",
					name: userData.name || "",
				});

				// Store full list from API for messaging (all permissions the plant user has)
				setAllSectorsFromApi(userData.sectors || []);

				// Populate sector data - only sectors where the current user can see at least one plant.
				// Do not show a sector at all if the logged-in user has no permission to see any of its plants (e.g. Engine).
				if (userData.sectors && userData.sectors.length > 0) {
					const sectorDataMap: Record<string, SectorData> = {};
					userData.sectors
						.filter((sector) =>
							accessibleSectors.some((fs) => fs.id === sector.sectorId)
						)
						.forEach((sector) => {
							// Filter plants: only show those the plant user has at least one permission to, and that the current user can manage
							const filteredPlants = sector.plants.filter((plant) => {
								if (!plant.permissions || plant.permissions.length === 0) return false;
								if (permissions.isSectorAdmin) {
									// Sector Admin: Check if they have permission to manage users for this plant
									const sectorInfo = accessibleSectors.find((s) => s.id === sector.sectorId);
									if (!sectorInfo) return false;
									return permissions.hasPermissionForPlants(
										[plant.plantId],
										OBJECT_ID_MANAGE_USER
									);
								} else if (permissions.isPlantUsersAccessManager) {
									// Plant Users Access Manager: Only show plants they can manage
									return permissions.manageablePlants.some(
										(mp) => mp.id === plant.plantId
									);
								}
								return false;
							});
							// Only include sector if at least one plant is visible to the current user
							if (filteredPlants.length > 0) {
								sectorDataMap[sector.sectorId] = {
									...sector,
									plants: filteredPlants,
								};
							}
						});
					const filteredSectorIds = Object.keys(sectorDataMap);
					setSelectedSectors(filteredSectorIds);
					setSectorData(sectorDataMap);
					// Only count sectors that have at least one plant with permissions in the form.
					const initialValidSectorIds = Object.entries(sectorDataMap)
						.filter(
							([, s]) =>
								s.plants.length > 0 &&
								s.plants.some((p) => p.permissions.length > 0)
						)
						.map(([id]) => id);
					setInitialSectorCount(initialValidSectorIds.length);
					setInitialSectorIds(initialValidSectorIds);
					// Snapshot for "has form changed" (only valid sectors)
					const initialValidMap: Record<string, SectorData> = {};
					Object.entries(sectorDataMap).forEach(([id, s]) => {
						if (s.plants.length > 0 && s.plants.every((p) => (p.permissions?.length ?? 0) > 0)) {
							initialValidMap[id] = s;
						}
					});
					setInitialSectorDataSnapshot(initialValidMap);
				} else {
					setInitialSectorCount(0);
					setInitialSectorIds([]);
					setInitialSectorDataSnapshot(null);
					setAllSectorsFromApi(userData.sectors || []);
				}

			} catch (err: any) {
				const errorMsg =
					err?.response?.data?.message ||
					err?.message ||
					"Failed to load user data";
				setError(errorMsg);
				if (err?.response?.status === 404) {
					snack.open(result.Err("User does not exist."));
					onClose();
				}
			} finally {
				setLoading(false);
				setInitialLoad(false);
			}
		};

		loadUserData();
	}, [open, gmin, api, snack, onClose, permissions, accessibleSectors]);

	useEffect(() => {
		if (!open) setInitialSectorDataSnapshot(null);
	}, [open]);

	const handleAddSector = useCallback(() => {
		if (!selectedSectorForAdd || selectedSectors.includes(selectedSectorForAdd)) {
			return;
		}
		setSelectedSectors((prev) => [...prev, selectedSectorForAdd]);
		setSectorData((prev) => ({
			...prev,
			[selectedSectorForAdd]: {
				sectorId: selectedSectorForAdd,
				plants: [],
			},
		}));
		setSelectedSectorForAdd("");
	}, [selectedSectorForAdd, selectedSectors]);

	const handleRemoveSector = useCallback((sectorId: string) => {
		setSelectedSectors((prev) => prev.filter((id) => id !== sectorId));
		setSectorData((prev) => {
			const newData = { ...prev };
			delete newData[sectorId];
			return newData;
		});
	}, []);

	const handleSectorDataChange = useCallback(
		(sectorId: string, data: SectorData) => {
			setSectorData((prev) => ({
				...prev,
				[sectorId]: data,
			}));
		},
		[]
	);

	const isFormValid = useMemo(() => {
		// Check all required fields (gmid not required for edit)
		// email and gmin are read-only, so they should always be present if loaded
		if (
			!formData.username ||
			!formData.email ||
			!formData.gmin ||
			!formData.name
		) {
			return false;
		}

		// Require at least one sector
		const sectors = Object.values(sectorData);
		if (sectors.length === 0) return false;

		// Every sector must have at least one plant, and every plant must have at least one permission
		const everySectorValid = sectors.every(
			(sector) =>
				sector.plants.length > 0 &&
				sector.plants.every((plant) => (plant.permissions?.length ?? 0) > 0)
		);
		return everySectorValid;
	}, [formData, sectorData]);

	// Current valid sector count (every sector has at least one plant and every plant has at least one permission)
	const validSectorCount = useMemo(() => {
		return Object.values(sectorData).filter(
			(sector) =>
				sector.plants.length > 0 &&
				sector.plants.every((plant) => (plant.permissions?.length ?? 0) > 0)
		).length;
	}, [sectorData]);

	// True when the user has changed the form (valid sectors differ from initial load)
	const hasFormChanged = useMemo(() => {
		if (initialSectorDataSnapshot === null) return false;
		return serializeValidSectorData(sectorData) !== serializeValidSectorData(initialSectorDataSnapshot);
	}, [sectorData, initialSectorDataSnapshot]);

	// At least one sector was removed (show delete message)
	const anySectorRemoved = initialSectorCount > 0 && validSectorCount < initialSectorCount;
	// At least one sector was explicitly removed from the form (user clicked Remove Sector) — show confirmation even if they later add an incomplete sector
	const anySectorExplicitlyRemoved = initialSectorIds.some(
		(id) => !selectedSectors.includes(id)
	);
	// All sectors in the form are removed (used for isLastSector)
	const allSectorsRemoved = validSectorCount === 0 && initialSectorCount > 0;

	// Clear the confirmation checkbox when user adds back all removed sectors
	useEffect(() => {
		if (!anySectorExplicitlyRemoved) {
			setDeleteConfirmationChecked(false);
		}
	}, [anySectorExplicitlyRemoved]);

	// Full sector count from API (all permissions the plant user has)
	const fullSectorCount = allSectorsFromApi.length;
	// Is this the plant user's only sector (losing it = lose access to MCC)
	const isLastSector = fullSectorCount === 1 && allSectorsRemoved;

	// Summary of all permissions for delete confirmation message (sector names where we know them)
	const allPermissionsSummary = useMemo(() => {
		if (fullSectorCount === 0) return "";
		const knownNames = allSectorsFromApi
			.map((s) => accessibleSectors.find((a) => a.id === s.sectorId)?.name)
			.filter((n): n is string => Boolean(n));
		const otherCount = fullSectorCount - knownNames.length;
		if (knownNames.length === 0) {
			return `${fullSectorCount} sector(s) in total`;
		}
		if (otherCount <= 0) {
			return `${fullSectorCount} sector(s): ${knownNames.join(", ")}`;
		}
		return `${fullSectorCount} sector(s): ${knownNames.join(", ")} and ${otherCount} other(s) you cannot manage`;
	}, [allSectorsFromApi, accessibleSectors, fullSectorCount]);

	// IDs of sectors that were removed (had content on load, no longer have valid content)
	const removedSectorIds = useMemo(() => {
		const currentValidIds = Object.entries(sectorData)
			.filter(
				([, s]) =>
					s.plants.length > 0 &&
					s.plants.every((p) => (p.permissions?.length ?? 0) > 0)
			)
			.map(([id]) => id);
		return initialSectorIds.filter((id) => !currentValidIds.includes(id));
	}, [sectorData, initialSectorIds]);

	// Only show "remove all permissions / will be removed from MCC" when the user has explicitly removed every sector (none left in form).
	// Do not show when validSectorCount is 0 due to incomplete sectors (e.g. RESS has some plants with permissions, Engine empty).
	// Do not show when the user has invisible sectors (API returned more sectors than we show) – they may still have access we can't see.
	const wouldHaveNoAccessAfterSave = useMemo(() => {
		if (validSectorCount > 0) return false;
		if (selectedSectors.length > 0) return false; // Sectors still in form (e.g. incomplete) – not "all removed"
		const hasAnyPlantWithPermission = Object.values(sectorData).some((s) =>
			s.plants.some((p) => (p.permissions?.length ?? 0) > 0)
		);
		if (hasAnyPlantWithPermission) return false; // User still has permissions in form
		// User has sectors we couldn't show (no visible plants) – don't claim they'd lose MCC access
		if (allSectorsFromApi.length > initialSectorIds.length) return false;
		return WouldUserHaveNoAccessAfterChange(
			fullUserSectors?.sectorIds ?? null,
			removedSectorIds,
			validSectorCount
		);
	}, [
		fullUserSectors?.sectorIds,
		removedSectorIds,
		validSectorCount,
		selectedSectors.length,
		sectorData,
		allSectorsFromApi.length,
		initialSectorIds.length,
	]);

	// Deleted sector names for the confirmation message (always show names when available)
	const removedSectorNames = useMemo(() => {
		return removedSectorIds
			.map((id) => accessibleSectors.find((s) => s.id === id)?.name ?? id)
			.filter(Boolean);
	}, [removedSectorIds, accessibleSectors]);

	// Incomplete sectors (no plants or plant without permissions) — used for alert and to hide RemoveSectorsConfirmation
	const incompleteSectorsWarning = useMemo(
		() => GetIncompleteSectorsWarning(sectorData, accessibleSectors, sectorTypePlants),
		[sectorData, accessibleSectors, sectorTypePlants]
	);

	const handleSave = useCallback(async () => {
		if (!gmin) return;

		// Sectors that have at least one plant and every plant has at least one permission
		const validSectors = Object.values(sectorData).filter(
			(sector) =>
				sector.plants.length > 0 &&
				sector.plants.every((plant) => (plant.permissions?.length ?? 0) > 0)
		);

		// A plant user cannot remove themselves entirely when that would leave them with no access to MCC
		if (isEditingSelf && wouldHaveNoAccessAfterSave) {
			snack.open(result.Err("You cannot remove all your own plant user access."));
			return;
		}

		// Require confirmation when any sector was explicitly removed (user clicked Remove Sector)
		if (anySectorExplicitlyRemoved && !deleteConfirmationChecked) {
			return;
		}

		try {
			setSaving(true);
			setError("");

			const { roleObjToAdd, roleObjToRemove } = ComputeRoleObjDelta(
				initialSectorDataSnapshot,
				sectorData
			);

			const request: UpdatePlantUserRequest = {
				roleObjToAdd,
				roleObjToRemove,
			};

			await api.UpdatePlantUser(gmin, request);
			snack.open(result.Ok(
				validSectors.length === 0
					? "Plant user deleted successfully"
					: "Plant user updated successfully"
			));
			onSuccess?.();
			onClose();
		} catch (err: any) {
			const errorMsg =
				err?.response?.data?.errorList?.[0]?.message ||
				err?.response?.data?.message ||
				err?.message ||
				(validSectors.length === 0
					? "Failed to delete plant user"
					: "Failed to update plant user");
			setError(errorMsg);
			snack.open(result.Err(errorMsg));
		} finally {
			setSaving(false);
		}
	}, [sectorData, initialSectorDataSnapshot, anySectorExplicitlyRemoved, deleteConfirmationChecked, isEditingSelf, wouldHaveNoAccessAfterSave, api, snack, onClose, onSuccess, gmin]);


	return (
		<UserManagementDialogShell
			open={open}
			onClose={onClose}
			title="Edit Plant User Access"
			maxWidth="md"
			loading={initialLoad || loading}
			error={error}
			contentSx={{ mt: 1 }}
			primaryButton={{
				label: wouldHaveNoAccessAfterSave ? "Delete" : "Save",
				onClick: handleSave,
				disabled: (() => {
					if (saving) return true;
					// Incomplete sectors (checkbox hidden): disable Save until they fix or remove those sectors
					if (incompleteSectorsWarning.omittedSectorNames.length > 0) return true;
					// A plant user cannot remove themselves entirely when that would leave them with no access (align with Alert and handleSave)
					if (isEditingSelf && wouldHaveNoAccessAfterSave) return true;
					// Full delete: no valid sectors, require confirmation
					if (validSectorCount === 0) return !deleteConfirmationChecked;
					// Update: need at least one valid sector and a change (or explicitly removed sectors with confirmation)
					const hasChangeOrRemoval = hasFormChanged || anySectorExplicitlyRemoved;
					const removalConfirmed = !anySectorExplicitlyRemoved || deleteConfirmationChecked;
					return !hasChangeOrRemoval || !removalConfirmed;
				})(),
				loading: saving,
				destructive: wouldHaveNoAccessAfterSave,
			}}
		>
			<Typography sx={{ fontWeight: 600, fontSize: "0.9rem", mb: 0.5, textAlign: "left" }}>
				Plant User: {formData.name}
			</Typography>
			{isEditingSelf && wouldHaveNoAccessAfterSave && (
				<Alert severity="info" sx={{ mt: 1, mb: 1 }}>
					You cannot remove all your own plant user access.
				</Alert>
			)}
			<input type="hidden" value={formData.username} />
			<Box>
				<Box display="flex" alignItems="center" gap={1} mb={2}>
					<Typography variant="body1" sx={{ minWidth: 80 }}>
						Sector:
					</Typography>
					<FormControl size="small" sx={{ minWidth: 120, ...GetSelectStyle() }}>
						<InputLabel>Sector</InputLabel>
						<Select
							value={selectedSectorForAdd}
							label="Sector"
							onChange={(e) => setSelectedSectorForAdd(e.target.value)}
							disabled={
								loading ||
								saving ||
								!sectorsAvailableForAdd.some((s) => !selectedSectors.includes(s.id))
							}
						>
							{sectorsAvailableForAdd
								.filter((s) => !selectedSectors.includes(s.id))
								.map((sector) => (
									<MenuItem key={sector.id} value={sector.id}>
										{sector.name}
									</MenuItem>
								))}
						</Select>
					</FormControl>
					<Button
						variant="contained"
						onClick={handleAddSector}
						disabled={
							!selectedSectorForAdd ||
							selectedSectors.includes(selectedSectorForAdd) ||
							loading ||
							saving
						}
						startIcon={<Typography sx={{ color: "white" }}>+</Typography>}
						sx={{ color: "white", minWidth: 140 }}
					>
						Add Sector
					</Button>
				</Box>

				{selectedSectors.map((sectorId) => {
					const sector = accessibleSectors.find((s) => s.id === sectorId);
					if (!sector) return null;
					return (
					<SectorTypePlantPermissionTable
						key={sectorId}
						sector={sector}
						plants={sectorTypePlants[sectorId] || []}
							data={sectorData[sectorId] || { sectorId, plants: [] }}
							onDataChange={(data) => handleSectorDataChange(sectorId, data)}
							onRemove={() => handleRemoveSector(sectorId)}
							isSectorAdmin={permissions.isSectorAdmin}
							loading={loading}
						/>
					);
				})}

				{selectedSectors.length === 0 && (
					<Alert severity="info">
						Click &quot;Add Sector&quot; to assign plants and permissions to this user.
					</Alert>
				)}

				{incompleteSectorsWarning.omittedSectorNames.length > 0 && (
					<Alert severity="warning" sx={{ mt: 1 }}>
						{GetIncompleteSectorsWarningMessage(incompleteSectorsWarning)}
					</Alert>
				)}

				{anySectorExplicitlyRemoved && incompleteSectorsWarning.omittedSectorNames.length === 0 && (
					<RemoveSectorsConfirmation
						checked={deleteConfirmationChecked}
						onCheckedChange={setDeleteConfirmationChecked}
						wouldLoseMcc={wouldHaveNoAccessAfterSave}
						userName={formData.name ?? ""}
						removedSectorNames={removedSectorNames}
						showDivider={true}
					/>
				)}
			</Box>
		</UserManagementDialogShell>
	);
}

